import { DatabaseIcon, DocumentTextIcon, ExternalLinkIcon, InformationCircleIcon, MailIcon, PencilIcon, XIcon } from "@heroicons/react/outline";
import { useContext, useEffect, useState } from "react";
import { useModal, WorkflowContext } from "../contexts";
import ToggleButton from "./ToggleButton";
import ProLabel from "./ProLabel";
import WorkflowDocumentOutputName from "./WorkflowDocumentOutptName";
import WorkflowSelectModal from "./WorkflowSelectModal";
import { Document } from "../interfaces";
import useUser from "../hooks/useUser";
import PayWallModalPro from "./PayWallModalPro";
import moment from "moment-timezone";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import TransitionWorkflowPage from "./transitions/TransitionWorkflowPage";
import TransitionWorkflowPageSideBar from "./transitions/TransitionWorkflowPageSideBar";
import TransitionWorkflowPageContent from "./transitions/TransitionWorkflowPageContent";
import { postData, putData } from "../backend";
import { useTranslation } from "react-i18next";
import { BLOCK_ICON_MAP, MIMETYPE_ICON_MAP } from "../utils";
import MicrosoftPickerButton from "./MicrosoftPickerButton";
import WorkflowSignatureConfig from "./WorkflowSignatureConfig";
import WorkflowPaymentConfig from "./WorkflowPaymentConfig";
import SmallSelectField from "./SmallSelectField";

function WorkflowDocumentMicrosoft() {
  const { user, hasPermission } = useUser();
  const { openModal } = useModal();
  const { t } = useTranslation();

  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();

  const workflowContext = useContext(WorkflowContext)
  const { workflow, mutateWorkflow } = workflowContext;

  const [updateFileState, setUpdateFileState] = useState<"INITIAL" | "UPDATING" | "ERROR">("INITIAL");

  const index = (() => {
    const hash = location.hash.split("#")[1];

    if (hash === "signable")
      return workflow.documents.indexOf(workflow.documents.filter(d => d.isSignable)[0]);

    return (parseInt(hash) || 1) - 1;
  })();

  const initialDocument = workflow.documents[index] ?? workflow.documents[0];
  const [document, setDocument] = useState<Document>();

  const [showNextStepMessage, setShowNextStepMessage] = useState(false);

  const isMac = navigator.platform.toLowerCase().indexOf("mac") >= 0;

  function closeNextStepMessage() {
    const key = "showNextStepMessageMap";
    const showNextStepMessageMapString = localStorage.getItem(key) ?? "{}";
    const showNextStepMessageMap: Record<string, boolean> = JSON.parse(showNextStepMessageMapString);

    showNextStepMessageMap[document!.id] = false;
    localStorage.setItem(key, JSON.stringify(showNextStepMessageMap));

    setShowNextStepMessage(false);
  }

  useEffect(() => {
    setDocument(initialDocument);

    // Check if the document was created before the "next step message" was introduced
    if (!initialDocument || moment(initialDocument.createdAt) < moment("2023-01-18"))
      return;

    const key = "showNextStepMessageMap";
    const showNextStepMessageMapString = localStorage.getItem(key) ?? "{}";
    const showNextStepMessageMap: Record<string, boolean> = JSON.parse(showNextStepMessageMapString);

    setShowNextStepMessage(showNextStepMessageMap[initialDocument.id] ?? true);
  }, [initialDocument]);

  async function updateDocument(field: string, value: any) {
    if (!document)
      return;

    const updatedDocument = { ...document, [field]: value };
    setDocument(updatedDocument);

    await putData(`/workflows/documents/${document!.id}/`, updatedDocument)
      .then(setDocument)
      .then(() => mutateWorkflow())
      .catch(console.error);
  }

  function toggleRemoveBranding(flag: boolean) {
    if (!user?.subscription?.featureRemoveBranding) {
      openModal(<PayWallModalPro />)
      return;
    }

    updateDocument("removeBranding", flag);
  }

  async function handleChangeFile(fileId: string) {
    await putData(`/microsoft/document-config/${document!.microsoftDocumentConfig!.id}/`, { fileId });
    await mutateWorkflow();
  }

  async function handleUpdateFile() {
    setUpdateFileState("UPDATING");

    postData(`/microsoft/document-config/${document!.microsoftDocumentConfig!.id}/update-file/`)
      .then(file => setDocument((d) => ({ ...d!, file })))
      .then(() => { setUpdateFileState("INITIAL"); })
      .catch(err => { console.error(err); setUpdateFileState("ERROR"); });
  }

  function openEditingWindows() {
    window.resizeTo(window.screen.width - 400, window.screen.height);
    window.open(document?.microsoftDocumentConfig?.fileUrl, document?.id);

    const popup = window.open(`/popup/${workflow.id}`, "source-fields-" + workflow.id, 'width=400,height=' + window.screen.height + ",left=" + (window.screen.width - 400) + ",top=0,scrollbars=yes,resizable=no")
    if (popup)
      setTimeout(() => popup.focus(), 500);
  }

  if (!document) {
    if (!location.pathname.endsWith("/document"))
      return null;

    return (
      <div className="flex flex-col items-center justify-center gap-4 h-full w-full">
        <div className="font-semibold font-gilroy text-gray-600 text-center w-96">
          {t("This workflow contains no document templates, would you like to add some?")}
        </div>
        <button className="btn btn-blue" onClick={() => openModal(<WorkflowSelectModal title={t("Add Workflow Template")} subtitle={t("Add a template to your workflow")} showDocuments workflowContext={workflowContext} />)}>
          {t("Add Document Template")}
        </button>
      </div>
    );
  }

  return (
    <TransitionWorkflowPage show={location.pathname.endsWith("/document")}>
      {hasPermission(workflow.team, "workflows.edit") &&
        <TransitionWorkflowPageSideBar>
          {!!workflow.source && <>

            {showNextStepMessage &&
              <div className="relative flex flex-col gap-1 p-2 text-sm text-white bg-blue rounded shadow">
                <div className="flex items-center font-gilroy font-semibold">
                  <InformationCircleIcon className="w-4 h-4 mr-2" />{t("Next Step")}
                </div>
                <div className="text-xs font-semibold">
                  {t("Click 'Copy' in the source table below then place your cursor in the document and click")} {isMac ? "⌘" : "Ctrl-"}V {t("to insert the tag into your document.")}
                </div>
                <XIcon className="absolute w-4 h-4 top-1 right-1 cursor-pointer" onClick={closeNextStepMessage} />
              </div>
            }

            <button className="btn btn-white" onClick={openEditingWindows}>
              Edit in OneDrive <ExternalLinkIcon className="h-4 w-4 ml-1" />
            </button>

            <div className="font-semibold text-sm text-gray-600 flex">
              {updateFileState === "UPDATING"
                ? <div>Updating...</div>
                : <>
                  {document.file
                    ? <div>{t("Last updated")} {moment(document.file.updatedAt).fromNow()}</div>
                    : <div className="text-yellow">{t("File has not been updated")}</div>
                  }
                  <button className="ml-auto hover:underline hover:text-blue" onClick={handleUpdateFile}>
                    {t("Update")}
                  </button>
                </>
              }
            </div>

            {updateFileState === "ERROR" &&
              <div className="flex items-center text-red font-semibold text-sm">
                {t("An error occurred while updating the file.")}
              </div>
            }

            <hr />

            <WorkflowDocumentOutputName initialValue={document.outputName} onEnter={(value) => updateDocument("outputName", value)} />

            <div className="flex justify-start items-center font-gilroy font-semibold text-sm w-full">
              <ToggleButton value={document.isSignable || document.createPdfCopy} onChange={(flag) => updateDocument(document.isSignable ? "isSignable" : "createPdfCopy", flag)} />
              <img className="w-5 h-5 ml-2 mr-1" src={MIMETYPE_ICON_MAP["application/pdf"]} alt="PDF File Icon" />
              <div className="font-semibold text-sm text-gray-600">Create {document.isSignable ? "Signable PDF" : "PDF copy"}</div>
            </div>


            {workflow.source.sourceType === "HUBSPOT" &&
              <div className="flex justify-start items-start font-gilroy font-semibold text-sm  w-full">
                <ToggleButton value={document.uploadToHubspot} onChange={(flag) => updateDocument("uploadToHubspot", flag)} />
                <div className="flex flex-col gap-1 mt-0.5 ml-2 mr-1">
                  <div className={`font-semibold text-sm ${(document.isSignable || document.createPdfCopy || document.createMicrosoftCopy) ? "text-gray-600" : "text-gray-400"}`}>
                    {t("Upload PDF/MS copy to HubSpot")}
                  </div>
                  {document.uploadToHubspot &&
                    <SmallSelectField label="File Access" value={document.uploadToHubspotAccess} onChange={(value) => updateDocument("uploadToHubspotAccess", value)}>
                      <option value="PRIVATE">{t("Private")}</option>
                      <option value="PUBLIC_INDEXABLE">{t("Public (Indexable)")}</option>
                      <option value="PUBLIC_NOT_INDEXABLE">{t("Public (Not Indexable)")}</option>
                    </SmallSelectField>
                  }
                </div>
              </div>
            }

            <div className="flex justify-start items-center font-gilroy font-semibold text-sm  w-full">
              <ToggleButton value={!!user?.subscription?.featureRemoveBranding && document.removeBranding} onChange={toggleRemoveBranding} />
              <div className={`font-semibold text-sm ml-2 mr-1 ${true ? "text-gray-600" : "text-gray-400"}`}>
                {t("Remove Portant PDF branding")}
              </div>
              <ProLabel />
            </div>

            <hr />
            <WorkflowSignatureConfig document={document} updateDocument={updateDocument} hideFields />
            <hr />
            <WorkflowPaymentConfig document={document} />

            {workflow.documents.length === 1 && <>
              <hr />
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center font-gilroy font-semibold text-gray-600">
                    <DocumentTextIcon className="w-6 h-6 mr-1" /> {t("Add another document")}
                  </div>
                  <div className="font-semibold text-xs text-gray-400">
                    {t("Create multiple documents in the same workflow")}
                  </div>
                </div>
                <button className="btn btn-white w-40 text-xs" onClick={() => openModal(<WorkflowSelectModal title={t("Add document")} subtitle={t("Add another document to this workflow")} showDocuments workflowContext={workflowContext} />)}>
                  {t("Add")}
                </button>
              </div>
            </>}

            {workflow.emails.length === 0 && <>
              <hr />
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center font-gilroy font-semibold text-gray-600">
                    <MailIcon className="w-6 h-6 mr-1" /> {t("Share document via Gmail")}
                  </div>
                  <div className="font-semibold text-xs text-gray-400">
                    {t("Automatically share created documents")}
                  </div>
                </div>
                <button className="btn btn-white w-40 text-xs" onClick={() => openModal(<WorkflowSelectModal title={t("Add sharing block")} subtitle={t("Automatically share created documents")} showEmails workflowContext={workflowContext} />)}>
                  {t("Add")}
                </button>
              </div>
            </>}

          </>}

          {!workflow.source && <>
            <div className="flex flex-col gap-4 justify-center h-full">
              <div className="flex items-center">
                <DatabaseIcon className="w-6 h-6 mr-2" />
                <div className="font-gilroy font-semibold text-lg">
                  {t("Connect a Source")}
                </div>
              </div>
              <div className="flex flex-col gap-2 font-semibold text-gray-600">
                {t("Create a batch of documents from a spreadsheet")}
                <div className="flex items-center gap-2">
                  <div className="h-px bg-gray-600 w-full" />
                  <div className="flex-shrink-0">{t("or")}</div>
                  <div className="h-px bg-gray-600 w-full" />
                </div>
                {t("Auto-Create documents from an event or form submission")}
              </div>
              <button className="btn btn-blue" onClick={() => openModal(<WorkflowSelectModal title={t("Select Source")} subtitle={t("Create or select a source where your data will come from")} showSources workflowContext={workflowContext} />)}>
                {t("Connect")}
              </button>
            </div>
          </>}
        </TransitionWorkflowPageSideBar>
      }

      <TransitionWorkflowPageContent>
        <div className="flex w-full items-center mb-2">
          <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
            <img className="h-10 w-10 rounded-md" src={BLOCK_ICON_MAP[document.documentType]} alt="Microsoft Excel File Icon" />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex flex-col h-full justify-center">
              <div className="flex text-sm font-semibold font-gilroy cursor-pointer hover:text-blue hover:underline" onClick={() => window.open(document.microsoftDocumentConfig?.fileUrl, "_blank")}>
                <div className="truncate max-w-[400px]">
                  {document.microsoftDocumentConfig?.fileName}
                </div>
                {document.microsoftDocumentConfig?.fileUrl &&
                  <ExternalLinkIcon className="h-4 w-4 ml-1 mb-1 group-hover:text-blue" />
                }
              </div>
              {hasPermission(workflow.team, "workflows.edit") &&
                <MicrosoftPickerButton className="flex items-center text-gray-500 text-sm font-gilroy font-medium hover:underline hover:text-gray-600" blockType={document.documentType} onPicked={handleChangeFile} allowedFileTypes={document.documentType === "MICROSOFT_WORD" ? ".doc,.docx" : ".ppt,.pptx"} style={{ padding: 0 }} disabled={!document.microsoftDocumentConfig?.fileId} openOnLoad={false}>
                  <PencilIcon className="h-4 mr-1" /><>{t("Change selected file")}</>
                </MicrosoftPickerButton>
              }
            </div>
          </div>
          <XIcon className="w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer" onClick={() => history.push(url)} />
        </div>
        <iframe title={t("Microsoft File Viewer")} height="1000" className="w-full h-full border border-gray-300 rounded overflow-hidden"
          src={document.microsoftDocumentConfig?.fileEmbedViewUrl} />
      </TransitionWorkflowPageContent>
    </TransitionWorkflowPage>
  );

}

export default WorkflowDocumentMicrosoft;
